// src/ai/tutorClient.ts — Client-Side Socratic Tutor Fetch Client (T-5.2.1, FR-AI-001, FR-AI-003)
// Handles hint dispatch with 3-second timeout, 1 retry, and seamless fallback activation.

import type { HintRequest, HintResponse } from './tutorTypes';
import { FallbackHintSystem } from './fallbackHints';
import { validateSocraticResponse } from './socraticPrompt';

export interface TutorClientOptions {
  endpoint?: string;
  timeoutMs?: number; // Default 3000ms (T-5.2.1)
  maxRetries?: number; // Default 1 (T-5.2.1)
}

export class TutorClient {
  private endpoint: string;
  private timeoutMs: number;
  private maxRetries: number;

  constructor(options: TutorClientOptions = {}) {
    this.endpoint = options.endpoint || '/api/tutor/hint';
    this.timeoutMs = options.timeoutMs ?? 3000;
    this.maxRetries = options.maxRetries ?? 1;
  }

  /**
   * Dispatches a hint request to the proxy endpoint with 1 retry and 3s timeout.
   * On failure or timeout, automatically engages FallbackHintSystem and logs failure (FR-AI-003).
   */
  async requestHint(request: HintRequest): Promise<HintResponse> {
    let lastError: Error | null = null;
    const totalAttempts = 1 + this.maxRetries; // Initial + 1 retry

    for (let attempt = 1; attempt <= totalAttempts; attempt++) {
      try {
        const response = await this.fetchWithTimeout(request);
        if (!response.ok) {
          throw new Error(`Proxy responded with status ${response.status}`);
        }

        const data: HintResponse = await response.json();

        // Validate Socratic constraints (FR-AI-002, CON-003)
        const validation = validateSocraticResponse(data.hint, data.interactivePrompt);
        if (!validation.valid) {
          console.warn('AI hint failed Socratic constraints:', validation.issues);
          // If constraints violated, use verified fallback
          return FallbackHintSystem.getFallback(request);
        }

        return {
          ...data,
          source: 'gemini',
        };
      } catch (err: any) {
        lastError = err instanceof Error ? err : new Error(String(err));
        // If there are attempts left, proceed to retry
        if (attempt < totalAttempts) {
          continue;
        }
      }
    }

    // Both initial attempt and retry failed -> log failure and deploy fallback (FR-AI-003)
    const errorMsg = lastError?.message || 'Network timeout or unreachable proxy';
    FallbackHintSystem.logFailure(request, errorMsg);
    return FallbackHintSystem.getFallback(request);
  }

  private async fetchWithTimeout(request: HintRequest): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeoutMs);

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(request),
        signal: controller.signal,
      });
      return response;
    } finally {
      clearTimeout(timeoutId);
    }
  }
}

// Global default singleton instance
export const tutorClient = new TutorClient();
